/**
 * Shared numeric-input rules for clinical form fields. Two flavours:
 *   - integer-only  (buildIntegerInputHandlers, INTEGER_ERROR_MESSAGE, ...)
 *   - decimal-allowed (buildDecimalInputHandlers, DECIMAL_ERROR_MESSAGE, ...)
 * Kept in one place so the messages, allow-lists, and regex stay in sync
 * wherever a numeric input is rendered.
 */

export const INTEGER_ERROR_MESSAGE = "This field contains only integers";
export const DECIMAL_ERROR_MESSAGE = "This field contains only numbers";

const NUMERIC_CONTROL_KEYS = new Set([
  "Backspace",
  "Delete",
  "Tab",
  "Escape",
  "Enter",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
]);

const INTEGER_CHAR = /^[-\d]$/;
const DECIMAL_CHAR = /^[-\d.]$/;
const INTEGER_STRING = /^-?\d+$/;
const DECIMAL_STRING = /^-?\d+(\.\d+)?$/;
const NON_INTEGER_CHARS = /[^-\d]/g;
const NON_DECIMAL_CHARS = /[^-\d.]/g;

export const isNumericControlKey = (key) => NUMERIC_CONTROL_KEYS.has(key);

export const isIntegerCharKey = (key) => INTEGER_CHAR.test(key);
export const isDecimalCharKey = (key) => DECIMAL_CHAR.test(key);

export const isIntegerString = (text) => INTEGER_STRING.test(text);
export const isDecimalString = (text) => DECIMAL_STRING.test(text);

export const stripNonIntegerChars = (txt) =>
  (txt || "").replace(NON_INTEGER_CHARS, "");
export const stripNonDecimalChars = (txt) =>
  (txt || "").replace(NON_DECIMAL_CHARS, "");

const buildNumericInputHandlers = (spec, { onError, onValid } = {}) => ({
  parser: spec.parser,
  onKeyDown: (e) => {
    if (isNumericControlKey(e.key)) {
      onValid?.();
      return;
    }
    if (e.ctrlKey || e.metaKey) return;
    if (spec.charKeyTest(e.key)) {
      onValid?.();
      return;
    }
    e.preventDefault();
    onError?.(spec.message);
  },
  onPaste: (e) => {
    const text = (e.clipboardData?.getData("text") || "").trim();
    if (text && !spec.stringTest(text)) {
      onError?.(spec.message);
    }
  },
});

export const buildIntegerInputHandlers = (handlers) =>
  buildNumericInputHandlers(
    {
      parser: stripNonIntegerChars,
      charKeyTest: isIntegerCharKey,
      stringTest: isIntegerString,
      message: INTEGER_ERROR_MESSAGE,
    },
    handlers
  );

export const buildDecimalInputHandlers = (handlers) =>
  buildNumericInputHandlers(
    {
      parser: stripNonDecimalChars,
      charKeyTest: isDecimalCharKey,
      stringTest: isDecimalString,
      message: DECIMAL_ERROR_MESSAGE,
    },
    handlers
  );

/**
 * Human-readable "Allowed range" hint for a field with optional min/max.
 * Returns null when neither bound is configured.
 */
export const formatNumericRange = ({ min, max }) => {
  if (min != null && max != null) return `${min} – ${max}`;
  if (min != null) return `Min ${min}`;
  if (max != null) return `Max ${max}`;
  return null;
};
